import { IMessage, spLogTags } from '@eventr/common'
import { Rocketchat } from '@rocket.chat/sdk'
import _ from 'underscore'
import { chatStore } from '../chatStore'

// mutliple typing status can come at the same time they should be processed in order.
let typingHandlerLock = 0
export default class EmbeddedChatApi {
    host: string
    rcClient: Rocketchat
    onMessageCallbacks: ((message: any) => void)[]
    onMessageDeleteCallbacks: ((messageId: string) => void)[]
    onTypingStatusCallbacks: ((users: string[]) => void)[]
    onActionTriggeredCallbacks: ((data: any) => void)[]
    onUiInteractionCallbacks: ((data: any) => void)[]
    typingUsers: string[]
    userID: string
    authToken: string
    ready: boolean

    constructor(host: string, userID: string, authToken: string) {
        this.host = host
        this.rcClient = new Rocketchat({
            protocol: 'ddp',
            host: this.host,
            useSsl: !/http:\/\//.test(host),
            reopen: 20000,
        })
        this.onMessageCallbacks = []
        this.onMessageDeleteCallbacks = []
        this.onTypingStatusCallbacks = []
        this.typingUsers = []
        this.onActionTriggeredCallbacks = []
        this.onUiInteractionCallbacks = []
        this.userID = userID
        this.authToken = authToken
        this.ready = false
        this.connect()
    }

    getHost() {
        return this.host
    }

    /**
     * All subscriptions are implemented here.
     * TODO: Add logic to call thread message event listeners. To be done after thread implementation
     */

    async connect() {
        try {
            await this.close() // before connection, all previous subscriptions should be cancelled
            await this.rcClient.connect({})
            const token = this.authToken
            await this.rcClient.resume({ token })
            this.ready = true
            // await this.rcClient.subscribeRoom(this.rid)
            await this.rcClient.onMessage((data: any) => {
                if (!data) {
                    return
                }
                const message = JSON.parse(JSON.stringify(data))

                this.onMessageCallbacks.map((callback) => callback(message))
            })

            await this.rcClient.subscribeNotifyUser()
        } catch (err) {
            await this.close()
        }
    }

    async addMessageListener(callback: (message: any) => void) {
        const idx = this.onMessageCallbacks.findIndex((c) => c === callback)
        if (idx !== -1) {
            this.onMessageCallbacks[idx] = callback
        } else {
            this.onMessageCallbacks.push(callback)
        }
    }

    async removeMessageListeners() {
        this.onMessageCallbacks = []
    }

    async addMessageDeleteListener(callback: (messageId: string) => void) {
        const idx = this.onMessageDeleteCallbacks.findIndex(
            (c) => c === callback
        )
        if (idx !== -1) {
            this.onMessageDeleteCallbacks[idx] = callback
        } else {
            this.onMessageDeleteCallbacks.push(callback)
        }
    }

    async removeMessageDeleteListener(callback: (messageId: string) => void) {
        this.onMessageDeleteCallbacks = this.onMessageDeleteCallbacks.filter(
            (c) => c !== callback
        )
    }

    async addTypingStatusListener(callback: (users: string[]) => void) {
        const idx = this.onTypingStatusCallbacks.findIndex(
            (c) => c === callback
        )
        if (idx !== -1) {
            this.onTypingStatusCallbacks[idx] = callback
        } else {
            this.onTypingStatusCallbacks.push(callback)
        }
    }

    async removeTypingStatusListener(callback: (users: string[]) => void) {
        this.onTypingStatusCallbacks = this.onTypingStatusCallbacks.filter(
            (c) => c !== callback
        )
    }

    async addActionTriggeredListener(callback: (data: any) => void) {
        const idx = this.onActionTriggeredCallbacks.findIndex(
            (c) => c === callback
        )
        if (idx !== -1) {
            this.onActionTriggeredCallbacks[idx] = callback
        } else {
            this.onActionTriggeredCallbacks.push(callback)
        }
    }

    async removeActionTriggeredListener(callback: (data: any) => void) {
        this.onActionTriggeredCallbacks =
            this.onActionTriggeredCallbacks.filter((c) => c !== callback)
    }

    async addUiInteractionListener(callback: (data: any) => void) {
        const idx = this.onUiInteractionCallbacks.findIndex(
            (c) => c === callback
        )
        if (idx !== -1) {
            this.onUiInteractionCallbacks[idx] = callback
        } else {
            this.onUiInteractionCallbacks.push(callback)
        }
    }

    async removeUiInteractionListener(callback: (data: any) => void) {
        this.onUiInteractionCallbacks = this.onUiInteractionCallbacks.filter(
            (c) => c !== callback
        )
    }

    handleTypingEvent({
        typingUser,
        isTyping,
    }: {
        typingUser: string
        isTyping: boolean
    }) {
        // don't wait for more than 2 seconds. Though in practical, the waiting time is insignificant.
        setTimeout(() => {
            typingHandlerLock = 0
        }, 2000)
        // eslint-disable-next-line no-empty
        while (typingHandlerLock) {}
        typingHandlerLock = 1
        // move user to front if typing else remove it.
        const idx = this.typingUsers.indexOf(typingUser)
        if (idx !== -1) {
            this.typingUsers.splice(idx, 1)
        }
        if (isTyping) {
            this.typingUsers.unshift(typingUser)
        }
        typingHandlerLock = 0
        const newTypingStatus = _.clone(this.typingUsers)
        try {
            this.onTypingStatusCallbacks.forEach((callback) =>
                callback(newTypingStatus)
            )
        } catch {
            spLogTags('Error sending typing status', ['rocketchat', 'ERROR'])
        }
    }

    async close() {
        await this.rcClient.unsubscribeAll()
        await this.rcClient.disconnect()
    }

    async me() {
        try {
            const response = await fetch(`${this.host}/api/v1/me`, {
                headers: {
                    'Content-Type': 'application/json',
                    'X-Auth-Token': this.authToken,
                    'X-User-Id': this.userID,
                },
                method: 'GET',
            })
            const resp = await response.json()
            chatStore.value?.set('me', this.host, resp)
            return resp
        } catch (err) {
            console.error(err)
        }
    }

    async getUsersInfo(userId: string) {
        try {
            const response = await fetch(
                `${this.host}/api/v1/users.info?userId=${userId}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Auth-Token': this.authToken,
                        'X-User-Id': this.userID,
                    },
                    method: 'GET',
                }
            )
            return await response.json()
        } catch (err) {
            console.error(err)
        }
    }
    async getUsersList(
        options: {
            query?: object | undefined
            field?: object | undefined
        } = {
            query: undefined,
            field: undefined,
        }
    ) {
        const query = options?.query
            ? `&query=${JSON.stringify(options.query)}`
            : ''
        const field = options?.field
            ? `&fields=${JSON.stringify(options.field)}`
            : ''
        try {
            const users = await fetch(
                `${this.host}/api/v1/users.list?${query}${field}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Auth-Token': this.authToken,
                        'X-User-Id': this.userID,
                    },
                    method: 'GET',
                }
            )
            const resp = await users.json()
            //we store with the key as host+rid so with each room we need to load again we could probably remove the this.rid part
            // and merge the users with the existing users in the store but this is a quick fix for now
            chatStore.value?.set('users', this.host, resp)
            return resp
        } catch (err) {
            console.log(err)
        }
    }

    async triggerBlockAction({
        type,
        actionId,
        appId,
        rid,
        mid,
        viewId,
        container,
        ...rest
    }: any) {
        try {
            const triggerId = Math.random().toString(32).slice(2, 16)

            const payload = rest.payload || rest

            const response = await fetch(
                `${this.host}/api/apps/ui.interaction/${appId}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Auth-Token': this.authToken,
                        'X-User-Id': this.userID,
                    },
                    method: 'POST',
                    body: JSON.stringify({
                        type: 'blockAction',
                        actionId,
                        payload,
                        container,
                        mid,
                        rid,
                        triggerId,
                        viewId,
                    }),
                }
            )
            const data = await response.json()
            this.onActionTriggeredCallbacks.map((cb) => cb(data))
            return data
        } catch (e) {
            console.error(e)
        }
    }

    async getCommandsList() {
        const response = await fetch(`${this.host}/api/v1/commands.list`, {
            headers: {
                'Content-Type': 'application/json',
                'X-Auth-Token': this.authToken,
                'X-User-Id': this.userID,
            },
            method: 'GET',
        })
        const data = await response.json()
        return data
    }

    /**
     * @param {boolean} anonymousMode
     * @param {Object} options This object should include query or fields.
     * query - json object which accepts MongoDB query operators.
     * fields - json object with properties that have either 1 or 0 to include them or exclude them
     * @returns messages
     */
    async getMessages(
        rid: string,
        roomType: string,
        anonymousMode = false,
        options: {
            query?: object | undefined
            field?: object | undefined
            offset?: Number | undefined
            count?: Number | undefined
            sort?: object | undefined
        } = {
            query: undefined,
            field: undefined,
            offset: undefined,
            count: undefined,
            sort: undefined,
        }
    ) {
        const endp = anonymousMode ? 'anonymousread' : 'messages'
        const query = options?.query
            ? `&query=${JSON.stringify(options.query)}`
            : ''
        const field = options?.field
            ? `&field=${JSON.stringify(options.field)}`
            : ''
        const offset = options?.offset
            ? `&offset=${JSON.stringify(options.offset)}`
            : ''
        const count = options?.count
            ? `&count=${JSON.stringify(options.count)}`
            : ''
        const sort = options?.sort
            ? `&sort=${JSON.stringify(options.sort)}`
            : ''
        try {
            const typeUrl =
                roomType === 'c' ? `channels.${endp}` : 'im.messages'
            const messages = await fetch(
                `${this.host}/api/v1/${typeUrl}?roomId=${rid}${query}${field}${offset}${count}${sort}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Auth-Token': this.authToken,
                        'X-User-Id': this.userID,
                    },
                    method: 'GET',
                }
            )
            const resp = await messages.json()
            resp.messages.reverse()
            const returnTotal = resp.total
            const returnCount = resp.count
            const returnOffset = resp.offset
            chatStore.value?.set('messages', this.host + rid, resp.messages)
            return {
                messages: resp.messages as IMessage[],
                total: returnTotal,
                count: returnCount,
                offset: returnOffset,
            }
        } catch (err) {
            console.log(err)
        }
    }
}
